import {
  Modal, Table, Button, Radio, Alert,
} from '@roo/roo';
import { RadioGroupProps } from '@roo/roo/Radio';
import React, { useEffect, useState } from 'react';
import { Column } from '@roo/roo/Table';
import { Link } from 'react-router-dom';
import { queryVoteRecord, submitVote } from '@assets/apis/review';
import { toastSuccess, toastFail } from '@utils/utils';

import './voteModal.less';

// roo的radio没有导出group类型
const RadioGroup = (Radio as any).Group as React.ComponentType<RadioGroupProps>;
enum OrganizationType {
  COMMON = 0, // 一般企业
  GOV, // 政府
  INDIV, // 个体户
  PUBLIC_UNITLITY, // 事业单位
  ORG, // 社会团体
  OTHERS // 其他
}

enum ProjectType {
  SCIENTIST = 1,
  INNOVATION
}

enum Vote {
  DENY = 0,
  PASS
}

enum VoteType {
  SELF,
  DELEGATION
}
interface VoteModalProps {
  projectType: ProjectType;
  groupId: string;
  roundId: string;
  groupName: string;
  voteAvailable: boolean;
  visible: boolean;
  needReview: boolean;
  needVote: boolean;
  onVisibleChange: (v: boolean) => void;
  reloadList: () => Promise<void>;
  setVoteAvailable: (v: boolean) => void;
  setVoteDone: (v: boolean) => void;
}

const OrgName = {
  [OrganizationType.COMMON]: '一般企业',
  [OrganizationType.GOV]: '政府机关',
  [OrganizationType.INDIV]: '个体工商户',
  [OrganizationType.PUBLIC_UNITLITY]: '事业单位',
  [OrganizationType.ORG]: '社会团体',
  [OrganizationType.OTHERS]: '其他机构',
};

const getOrgType = (type: OrganizationType) => OrgName[type] || '-';

const VoteModal = (props: VoteModalProps) => {
  const {
    projectType, groupId, roundId, voteAvailable, setVoteAvailable, visible, onVisibleChange, reloadList, groupName, needReview, needVote, setVoteDone,
  } = props;
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [voteQuota, setVoteQuota] = useState({ passVoteNum: 0, denyVoteNum: 0 });
  const [voted, setVoted] = useState([] as { projectNo: string; vote: Vote }[]);
  const [voteType, setVoteType] = useState(VoteType.SELF);

  const currentPass = voted.filter((v) => v.vote === Vote.PASS).length;
  const currentDeny = voted.filter((v) => v.vote === Vote.DENY).length;
  const voteNotDone = voted.length < projects.length || currentPass > voteQuota.passVoteNum || currentDeny > voteQuota.denyVoteNum;

  useEffect(() => {
    const fetchRecord = async () => {
      setLoading(true);
      try {
        const res = await queryVoteRecord({ projectType, groupId, roundId });
        if (!res.projects) {
          return;
        }

        setProjects(res.projects);
        setVoteQuota({ passVoteNum: res.passVoteNum, denyVoteNum: res.denyVoteNum });
        setVoted(
          res.projects
            .filter((p) => p.vote != null)
            .map(({ projectNo, vote }) => ({ projectNo, vote })),
        );
        setVoteType(res.operatorType);
      } finally {
        setLoading(false);
      }
    };

    // 打开弹窗后再调用
    if (!visible || projects.length) {
      return;
    }
    fetchRecord();
  }, [visible]);

  const ScientistColumns: Column<any>[] = [
    {
      prop: 'scientistName', label: '申报人姓名', align: 'center', width: 120,
    },
    {
      prop: 'schoolDegree', label: '学校-学历', align: 'center', width: '15%',
    },
    {
      prop: 'studyArea', label: '所属学科领域', align: 'center', width: '20%',
    },
  ];

  const InnovationColumns: Column<any>[] = [
    {
      prop: 'organizationName', label: '项目机构名称', align: 'center', width: '15%',
    },
    {
      prop: 'reportDirection', label: '申报方向', align: 'center', width: '15%',
    },
    {
      prop: 'organizationType',
      label: '机构类型',
      align: 'center',
      width: '20%',
      render: (_, item) => getOrgType(item.organizationType),
    },
  ];

  const specifyColumns = projectType === ProjectType.SCIENTIST ? ScientistColumns : InnovationColumns;

  const scoreColumns: Column<any>[] = [{
    prop: 'score', label: '得分', align: 'center', width: 80,
  }];
  const specifyColumnsWithScore = needReview ? specifyColumns.concat(scoreColumns) : specifyColumns;

  const columns: Column<any>[] = [
    {
      prop: 'projectNo',
      label: '项目编号',
      align: 'center',
      width: 200,
      render: (_, item) => (
        voteAvailable
          ? (
            <Button
              type="brand-text"
              style={{ color: '#FF6A00' }}
            >
              <Link to={`/review/detail/${item.id}`} target="_blank">
                《
                {item.projectNo}
                》
              </Link>
            </Button>
          )
          : (
            <>
              《
              {item.projectNo}
              》
            </>
          )
      ),
    },

    ...specifyColumnsWithScore,

    {
      prop: 'date',
      align: 'center',
      label: '操作',
      width: 210,
      render: (_, item) => {
        const one = voted.find((v) => v.projectNo === item.projectNo);
        return (
          <RadioGroup
            value={one?.vote}
            onChange={(vote) => voteOne(item.projectNo, vote)}
            disabled={!voteAvailable}
          >
            <Radio value={Vote.PASS}>通过</Radio>
            <Radio value={Vote.DENY}>否决</Radio>
          </RadioGroup>
        );
      },
    },
  ];

  // 如果选择的通过/否决总数超过了配额，那么只提示，最后不让提交即可
  const voteOne = (projectNo: string, vote: Vote) => {
    if (currentPass >= voteQuota.passVoteNum && vote === Vote.PASS) {
      toastFail(`您已投出${currentPass}份通过票，请合理安排票型`);
    }

    if (currentDeny >= voteQuota.denyVoteNum && vote === Vote.DENY) {
      toastFail(`您已投出${currentDeny}份否决票，请合理安排票型`);
    }
    setVoted([...voted.filter((v) => v.projectNo !== projectNo), { projectNo, vote }]);
  };

  const submit = async () => {
    try {
      const res = await submitVote({ projects: voted, groupId, roundId: Number(roundId) });
      toastSuccess('投票成功');
      reloadList();
      setVoteAvailable(false);
      setVoteDone(true);
      onVisibleChange(false);
    } catch (e) { }
  };

  const assingTitle = (prefix: string) => `${prefix}-${groupName}`;

  return (
    <Modal
      visible={visible}
      title={voteAvailable ? assingTitle('投票') : assingTitle('查看票型')}
      className="vote-modal"
      confirmButtonProps={{ disabled: voteNotDone }}
      showCancelButton={voteAvailable}
      showConfirmButton={voteAvailable}
      onCancel={() => {
        onVisibleChange(false);
        /** 以便每次都能拿到最新的数据 */
        setProjects([]);
      }}
      onConfirm={submit}

    >
      {voteAvailable && (
        <Alert
          className="vote-modal__alert"
          type="warning"
          title={`您在此组别一共有${voteQuota.passVoteNum}票通过票，${voteQuota.denyVoteNum}票否决票，票型一旦提交则无法修改，请谨慎投票。`}
          showIcon={false}
        />
      )}
      <Table
        border
        stripe
        rowKey="projectNo"
        className="vote-modal__table"
        columns={columns}
        loading={loading}
        data={projects}
      />
      {voteType === VoteType.DELEGATION && <p className="vote-modal__notice">此投票为评委授权运营代投</p>}
    </Modal>
  );
};

export default VoteModal;
